/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

function removeNextDiv() {
  var swatchColorElements = document.querySelectorAll(
    ".collection-swatch-color",
  );
  swatchColorElements.forEach(function (swatchColorElement) {
    var nextElement = swatchColorElement.nextElementSibling;
    while (nextElement) {
      if (nextElement.tagName.toLowerCase() === "div") {
        nextElement.style.display = "none";
        break;
      }
      nextElement = nextElement.nextElementSibling;
    }
  });
}
function observeAndRemoveDivs() {
  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.type === "childList") {
        removeNextDiv();
      }
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

document.addEventListener("DOMContentLoaded", function () {
  removeNextDiv();
  observeAndRemoveDivs();
});

document.addEventListener("DOMContentLoaded", () => {
  const productBlocks = Array.from(document.querySelectorAll(".product-block"));
  productBlocks.forEach((block) => {
    const container = block.querySelector(".product-price");
    if (!container) return;
    const hasFakeSwatch = Boolean(container.querySelector(".swatch-colors"));
    if (hasFakeSwatch) return;
    const optionsList = container.querySelector(
      ".product-block-options__inner",
    );
    if (!optionsList || !optionsList.firstElementChild) return;
    optionsList.firstElementChild.classList.add("active-swatch");

    optionsList.addEventListener("click", (e) => {
      const item = e.target.closest(".product-block-options__item");
      if (!item) return;

      const currentActive = optionsList.querySelector(".active-swatch");
      if (currentActive) currentActive.classList.remove("active-swatch");

      item.classList.add("active-swatch");
    });
  });
});

// pdp mobile no-header display

(function () {
  if (window.location.href.indexOf("products") === -1) return;
  if (window.innerWidth >= 769) return;
  const header = document.querySelector(".section-header");
  if (!header) return;
  window.addEventListener(
    "scroll",
    function () {
      if (window.scrollY !== 0) {
        header.style.height = "55px";
        header.style.transform = "translateY(0)";
      } else {
        header.style.height = "0";
        header.style.transform = "translateY(-55px)";
      }
    },
    { passive: true },
  );
})();

// cart drawer upsell

document.addEventListener("change", (e) => {
  if (!e.target.matches(".cart-item-upsells .cart-upsell-select")) return;

  const selectedOptions = [];
  const optionsElement = e.target.closest(".cart-upsell-options");
  const currentForm = e.target.closest(
    ".js-product-form.product-purchase-form",
  );

  if (!currentForm) return;

  const variants = JSON.parse(
    currentForm.querySelector(".product-variants-dictionary").textContent,
  );
  const inputHidden = currentForm.querySelector(".hidden-variant-id-input");
  const addToCartButton = currentForm.querySelector(".add-to-cart");

  optionsElement.querySelectorAll(".cart-upsell-select").forEach((select) => {
    selectedOptions.push(select.value);
  });

  const matchingVariant = findMatchingVariant(variants, selectedOptions);

  if (matchingVariant) {
    inputHidden.value = matchingVariant.id;
    addToCartButton.disabled = !matchingVariant.available;
    addToCartButton.textContent = matchingVariant.available
      ? addToCartButton.dataset.addToCartText
      : "Sold out";
  } else {
    addToCartButton.textContent = "Unavailable";
    addToCartButton.disabled = true;
  }
});
function findMatchingVariant(variantsArray, selectedOptionsArray) {
  let variant;
  variantsArray.forEach((v) => {
    if (
      JSON.stringify([...v.options].sort()) ===
      JSON.stringify([...selectedOptionsArray].sort())
    ) {
      variant = v;
    }
  });
  return variant;
}
// custom collection quick-buy
document.addEventListener("change", (e) => {
  if (!e.target.closest(".bundle-option-selector .option-selector")) return;
  const currentForm = e.target.closest(
    ".js-product-form.product-purchase-form",
  );
  if (!currentForm) return;
  const variants = JSON.parse(
    currentForm.querySelector(".product-variants-dictionary").textContent,
  );
  const inputHidden = currentForm.querySelector(".hidden-variant-id-input");
  const addToCartButton = currentForm.querySelector(".add-to-cart");
  const selectedOptions = [];
  console.log("add to cart", addToCartButton);
  currentForm.querySelectorAll(".option-selector").forEach((selector) => {
    if (selector.dataset.selectorType === "dropdown") {
      const selected = selector.querySelector(
        '.custom-select__option[aria-selected="true"]',
      );
    } else {
      const checked = selector.querySelector("input.js-option:checked");
      selectedOptions.push(checked ? checked.value : null);
    }
  });
  const matchingVariant = findMatchingVariant(variants, selectedOptions);
  if (matchingVariant) {
    inputHidden.value = matchingVariant.id;
    addToCartButton.disabled = !matchingVariant.available;
    addToCartButton.textContent = matchingVariant.available
      ? addToCartButton.dataset.addToCartText
      : "Sold out";
  } else {
    addToCartButton.textContent = "Unavailable";
    addToCartButton.disabled = true;
  }
});
// open custom quickbuy
document.addEventListener("click", (e) => {
  if (e.target.classList.contains("add-to-bundle")) {
    const productBlock = e.target.closest(".product-block");
    const wasOpen = productBlock?.classList.contains("bundle-open");

    document
      .querySelectorAll(".product-block.bundle-open")
      .forEach((block) => block.classList.remove("bundle-open"));

    if (wasOpen) return;

    productBlock?.classList.add("bundle-open");

    const selector = productBlock?.querySelector(".bundle-option-selector");
    if (selector) {
      let scrolled = false;
      const scrollToSelector = () => {
        if (scrolled) return;
        scrolled = true;

        // fixed header covering the top of the viewport
        const headerHeight = 70;

        const rect = selector.getBoundingClientRect();
        if (window.matchMedia("(min-width: 768px)").matches) {
          let viewportBottom = window.innerHeight;
          const bar = document.querySelector(".bundle-bar-container");
          if (bar) {
            const barTop = bar.getBoundingClientRect().top;
            if (barTop < viewportBottom) viewportBottom = barTop;
          }

          if (rect.top >= headerHeight && rect.bottom <= viewportBottom) return;
        }

        const desired =
          window.scrollY +
          rect.top -
          (headerHeight +
            (window.innerHeight - headerHeight - rect.height) / 2);
        const maxScroll =
          document.documentElement.scrollHeight - window.innerHeight;
        const top = Math.max(0, Math.min(desired, maxScroll));
        window.scrollTo({ top, behavior: "smooth" });
      };
      selector.addEventListener("transitionend", function onEnd(ev) {
        if (ev.propertyName !== "transform") return;
        selector.removeEventListener("transitionend", onEnd);
        scrollToSelector();
      });

      setTimeout(scrollToSelector, 300);
    }

    return;
  }

  if (e.target.closest(".bundle-option-selector__close")) {
    e.target.closest(".product-block")?.classList.remove("bundle-open");
    return;
  }

  if (e.target.closest(".bundle-add-to-cart")) {
    e.target.closest(".product-block")?.classList.remove("bundle-open");
  }
});
document.addEventListener("on:cart:after-merge", (e) => {
  const cartDrawer = document.querySelector(".cart-drawer__content");
  if (cartDrawer) {
    cartDrawer.scrollTo({ top: 0, behavior: "smooth" });
  }
});

function initBundles() {
  const cartContexts = document.querySelectorAll("cart-form");

  cartContexts.forEach((cartContext) => {
    const bundleElements = Array.from(
      cartContext.querySelectorAll("[data-bundle]"),
    ).filter((el) => !el.closest(".merge-remove-item"));

    const bundleNames = [
      ...new Set(
        bundleElements
          .map((el) => el.dataset.bundle)
          .filter((name) => name !== ""),
      ),
    ];

    bundleNames.forEach((bundleName) => {
      const items = bundleElements.filter(
        (el) => el.dataset.bundle === bundleName,
      );
      updateBundleMessage(items);
    });
  });
}

function updateBundleMessage(items) {
  if (items.length === 0) return;

  const [bundleName, requiredQty, discount] =
    items[0].dataset.bundle.split("|");
  const bundleQuantity = Number(requiredQty);

  let quantity = 0;
  items.forEach((el) => {
    const input = el.querySelector(".js-quantity");
    if (input) {
      quantity += Number(input.value);
    }
  });

  console.log(
    "Bundle:",
    bundleName,
    "| qty:",
    quantity,
    "| required:",
    bundleQuantity,
  );
  //hide bundle label from cart
  // const message = items[items.length - 1].querySelector(".message");
  // if (message) {
  //   if (quantity < bundleQuantity) {
  //     const remaining = bundleQuantity - quantity;
  //     message.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7" y2="7"></line></svg> <span>Add ${remaining} more ${bundleName} and save ${discount}%</span>`;
  //   } else {
  //     message.innerHTML = "";
  //   }
  // }
}

// Initialize on page load
document.addEventListener("DOMContentLoaded", () => {
  initBundles();
});

// Re-initialize after cart DOM updates
document.addEventListener("on:cart:after-merge", () => {
  initBundles();
});

/**
 * Promotion banner orchestration.
 *
 * The promotion (bundle) banners and the free-shipping bar are mutually
 * exclusive — whichever bundle banner is visible hides the free-shipping bar,
 * and the free-shipping bar shows whenever no bundle banner is visible.
 *
 * Rules:
 *  - When a bundle is NEWLY activated (detected via cart:after-merge):
 *    1. Show "Congratulations" banner for CONGRATULATIONS_DURATION_MS (free-shipping bar hidden)
 *    2. If a different bundle is still pending — show "pending" banner
 *    3. Otherwise — show the free-shipping bar
 *  - When the drawer opens WITHOUT a new activation:
 *    - Show only the pending banner (if any), free-shipping bar hidden
 *    - Otherwise show the free-shipping bar immediately (no congratulations replay)
 */
let __prevActivatedLabel = "";
// Every bundle label that was activated as of the last check, delimited as "|label-a||label-b|".
// Only one "winner" label is ever displayed/congratulated at a time (see activated above), but
// with 2+ bundles active simultaneously the winner can change identity without anything new
// actually unlocking - e.g. bundle A's quantity drops below threshold while B stays activated,
// and B (already active, just not previously the winner) becomes the new winner. Comparing
// against this set instead of the single previous winner is what tells that apart from a real
// new activation.
let __prevActivatedLabelsSet = "";
let __pendingCongratulatoryLabel = null;
const __promotionTimeouts = [];
// How long "Congratulations" stays up before handing off to the pending banner / shipping bar
const CONGRATULATIONS_DURATION_MS = 1500;
// Handle of the hand-off timeout while the congratulations banner is counting down
let __congratulationsHandoffTimer = null;

function clearPromotionTimeouts() {
  __promotionTimeouts.forEach(clearTimeout);
  __promotionTimeouts.length = 0;
  __congratulationsHandoffTimer = null;
}

function getPromotionLabels() {
  const container = document.querySelector('[data-merge="promotion-banners"]');
  if (!container) return { pending: "", activated: "", activatedSet: "" };
  const [pending = "", activated = "", , , activatedSet = ""] = (
    container.dataset.mergeCache || ""
  ).split("§");
  return {
    pending: pending.trim(),
    activated: activated.trim(),
    activatedSet: activatedSet.trim(),
  };
}

function isLabelInActivatedSet(label, activatedSet) {
  return !!label && activatedSet.indexOf(`|${label}|`) !== -1;
}

function hideAllPromotionBanners() {
  document
    .querySelectorAll(
      ".cart-drawer__promotion-banners--activated, .cart-drawer__promotion-banners--pending",
    )
    .forEach((el) =>
      el.classList.remove("cart-drawer__promotion-banners--visible"),
    );
}

function setFreeShippingBarHidden(hidden) {
  const bar = document.querySelector(".free-shipping-bar");
  if (!bar) return;
  bar.classList.toggle("free-shipping-bar--hidden", hidden);
}

function runPromotionBannerSequence() {
  const activatedEl = document.querySelector(
    ".cart-drawer__promotion-banners--activated",
  );
  const pendingEl = document.querySelector(
    ".cart-drawer__promotion-banners--pending",
  );

  // A hand-off is already counting down and we were merely re-run by a re-render of the drawer
  // (cart merge, upsell refresh, ...). Re-apply the visible state to the possibly-new nodes but
  // keep the original timer - restarting it on every DOM change means it never fires.
  if (__congratulationsHandoffTimer !== null && activatedEl) {
    hideAllPromotionBanners();
    setFreeShippingBarHidden(true);
    activatedEl.classList.add("cart-drawer__promotion-banners--visible");
    return;
  }

  clearPromotionTimeouts();
  hideAllPromotionBanners();

  if (__pendingCongratulatoryLabel && activatedEl) {
    // Show congratulations first, then hand off to pending (if any) or free shipping
    setFreeShippingBarHidden(true);
    activatedEl.classList.add("cart-drawer__promotion-banners--visible");

    __congratulationsHandoffTimer = setTimeout(() => {
      __congratulationsHandoffTimer = null;
      __pendingCongratulatoryLabel = null;

      // The nodes may have been replaced by a re-render since this timer was armed
      hideAllPromotionBanners();
      const currentPendingEl = document.querySelector(
        ".cart-drawer__promotion-banners--pending",
      );

      if (currentPendingEl) {
        // A different bundle is still pending — switch to it
        currentPendingEl.classList.add(
          "cart-drawer__promotion-banners--visible",
        );
        setFreeShippingBarHidden(true);
      } else {
        // Nothing left to show — hand off to the free-shipping bar
        setFreeShippingBarHidden(false);
      }
    }, CONGRATULATIONS_DURATION_MS);
    __promotionTimeouts.push(__congratulationsHandoffTimer);
  } else if (pendingEl) {
    // No new activation — show pending, keep the free-shipping bar hidden
    pendingEl.classList.add("cart-drawer__promotion-banners--visible");
    setFreeShippingBarHidden(true);
  } else {
    // No pending bundle (activated-but-stale, or no bundle in cart at all) —
    // skip any congratulations replay and show the free-shipping bar directly
    setFreeShippingBarHidden(false);
  }
}

// Toggling an attribute that already has the wanted value still counts as a mutation for the
// cart-drawer observer below, so every DOM write from here has to be conditional.
function setHidden(el, hidden) {
  if (el.hidden !== hidden) el.hidden = hidden;
}

// Loyalty points are earned per pound of the shop currency (GBP), but the cart
// total rendered by Liquid is in the customer's presentment currency.
// Shopify.currency.rate is the shop -> active currency rate, so dividing by it
// takes us back to GBP. Matches how Smile awards points: floor(gbp * perUnit).
// Iterates the wrappers rather than [data-cart-points] so that an emptied cart -
// where Liquid renders the wrapper with nothing inside - hides it instead of
// leaving an empty padded box behind. The cart total has to stay on the inner
// element: theme.mergeNodes only swaps the wrapper's innerHTML, so a data
// attribute on the wrapper itself would go stale after a cart update.
function updateCartPoints() {
  document.querySelectorAll(".cart-points").forEach((wrapper) => {
    const el = wrapper.querySelector("[data-cart-points]");
    const totalCents = el ? parseInt(el.dataset.cartTotal, 10) : NaN;
    const perUnit = el ? parseFloat(el.dataset.pointsPerUnit) : NaN;
    if (
      !Number.isFinite(totalCents) ||
      !Number.isFinite(perUnit) ||
      perUnit <= 0
    ) {
      setHidden(wrapper, true);
      return;
    }

    const rate = parseFloat(window.Shopify?.currency?.rate) || 1;
    const shopAmount = totalCents / 100 / rate;
    const points = Math.floor(shopAmount * perUnit);

    // Write only when the value actually changes. Assigning textContent always replaces the
    // child text node, which is a childList mutation - and this function is called BY the
    // cart-drawer MutationObserver below, so an unconditional write re-triggers the observer
    // and loops forever (~20 re-runs/sec), which in turn kept restarting the promotion
    // banner countdown so "Congratulations" never handed off to the free-shipping bar.
    const valueEl = el.querySelector("[data-cart-points-value]");
    const nextValue = points.toLocaleString();
    if (valueEl && valueEl.textContent !== nextValue) {
      valueEl.textContent = nextValue;
    }
    setHidden(wrapper, points <= 0);
  });
}

function handleCartDrawerContentChanged() {
  const { activated, activatedSet } = getPromotionLabels();
  const drawer = document.querySelector("cart-drawer");
  const isOpen = drawer && drawer.hasAttribute("open");

  if (
    activated &&
    !isLabelInActivatedSet(activated, __prevActivatedLabelsSet)
  ) {
    __pendingCongratulatoryLabel = activated;
  }
  __prevActivatedLabel = activated;
  __prevActivatedLabelsSet = activatedSet;

  updateCartPoints();

  if (isOpen) {
    runPromotionBannerSequence();
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const { activated, activatedSet } = getPromotionLabels();
  __prevActivatedLabel = activated;
  __prevActivatedLabelsSet = activatedSet;
  updateCartPoints();
});

document.addEventListener("on:cart:after-merge", () => {
  handleCartDrawerContentChanged();
});

document.addEventListener("on:cart-drawer:after-open", () => {
  runPromotionBannerSequence();
});

// Safety net: some cart-add flows (e.g. cart upsells) replace the cart
// drawer's HTML directly instead of going through theme.mergeNodes, so
// on:cart:after-merge never fires for them. Watch the drawer for any
// structural change and re-run the same logic regardless of what caused
// it, so the promotion/free-shipping bars and manually-loaded line-item
// images never get left showing stale state.
(() => {
  const cartDrawerEl = document.querySelector("cart-drawer");
  if (!cartDrawerEl || typeof MutationObserver === "undefined") return;

  let debounceTimer;
  const observer = new MutationObserver(() => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      handleCartDrawerContentChanged();
      if (theme.manuallyLoadImages) {
        theme.manuallyLoadImages(cartDrawerEl);
      }
    }, 50);
  });
  observer.observe(cartDrawerEl, { childList: true, subtree: true });
})();

document.addEventListener("on:cart-drawer:after-close", () => {
  clearPromotionTimeouts();
  hideAllPromotionBanners();
});

// custom bundle bar
if (!customElements.get("bundle-bar")) {
  class BundleBar extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.onCartRefresh = this.scheduleRefresh.bind(this);
      this.onClick = this.handleClick.bind(this);
      document.addEventListener("on:cart:add", this.onCartRefresh);
      document.addEventListener("on:cart:after-merge", this.onCartRefresh);
      document.addEventListener("on:cart:change", this.onCartRefresh);
      this.addEventListener("click", this.onClick);

      this.section = this.closest(".bundle-bar-section");
      this.dockMq = window.matchMedia("(max-width: 768px)");
      this.onDockScroll = this.updateDock.bind(this);
      this.onDockMeasure = this.measureBarHeight.bind(this);
      this.onDockMqChange = this.applyDock.bind(this);
      this.dockMq.addEventListener("change", this.onDockMqChange);
      this.applyDock();
    }
    applyDock() {
      if (!this.section) return;
      if (this.dockMq.matches && !this.dockActive) {
        this.dockActive = true;
        window.addEventListener("scroll", this.onDockScroll, { passive: true });
        window.addEventListener("resize", this.onDockMeasure, {
          passive: true,
        });
        window.addEventListener("load", this.onDockMeasure);
        this.measureBarHeight();
        requestAnimationFrame(() => this.measureBarHeight());
        this.updateDock();
      } else if (!this.dockMq.matches && this.dockActive) {
        this.dockActive = false;
        window.removeEventListener("scroll", this.onDockScroll);
        window.removeEventListener("resize", this.onDockMeasure);
        window.removeEventListener("load", this.onDockMeasure);
        this.classList.remove("is-docked");
        this.section.style.removeProperty("--bundle-bar-h");
      }
    }
    measureBarHeight() {
      if (!this.section) return;
      const container = this.querySelector(".bundle-bar-container");
      if (!container) return;
      this.section.style.setProperty(
        "--bundle-bar-h",
        `${container.offsetHeight}px`,
      );
      this.updateDock();
    }
    updateDock() {
      if (!this.section) return;
      const docked =
        this.section.getBoundingClientRect().bottom <= window.innerHeight;
      this.classList.toggle("is-docked", docked);
    }
    async refresh() {
      const response = await fetch(
        `${window.location.pathname}?section_id=${this.sectionId}`,
      );
      const rawHtml = await response.text();
      const html = new DOMParser().parseFromString(rawHtml, "text/html");
      const fresh = html.querySelector("bundle-bar");
      if (!fresh) return;
      const newInner = fresh.innerHTML;
      if (newInner === this.innerHTML) return;
      this.innerHTML = newInner;
      if (this.dockActive) this.measureBarHeight();
    }
    scheduleRefresh() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), 50);
    }
    async handleClick(e) {
      const deleteButton = e.target.closest(".slot-delete");
      if (!deleteButton) return;
      e.preventDefault();
      if (this.isUpdating) return;
      this.isUpdating = true;
      const slot = deleteButton.closest(".slot");
      slot.classList.add("slot--loading");
      const key = deleteButton.dataset.key;
      const quantity = Number(deleteButton.dataset.qty) - 1;
      const updates = {
        [key]: quantity,
      };
      const analyticsItem =
        window.heistAnalytics?.itemFromElement(deleteButton);
      try {
        const response = await fetch(theme.routes.cartUpdate, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ updates }),
        });
        if (!response.ok) {
          console.error(response.status);
          slot.classList.remove("slot--loading");
          return;
        }
        document.dispatchEvent(
          new CustomEvent("on:cart:change", {
            bubbles: true,
            cancelable: false,
          }),
        );
        // Always one unit: the request sets qty - 1 and isUpdating blocks repeat clicks
        window.heistAnalytics?.pushRemoveFromCart(analyticsItem, 1);
      } catch (error) {
        console.error("Cart update error", error);
        slot.classList.remove("slot--loading");
      } finally {
        this.isUpdating = false;
      }
    }
    disconnectedCallback() {
      document.removeEventListener("on:cart:add", this.onCartRefresh);
      document.removeEventListener("on:cart:after-merge", this.onCartRefresh);
      document.removeEventListener("on:cart:change", this.onCartRefresh);
      this.removeEventListener("click", this.onClick);
      clearTimeout(this.refreshTimer);
      window.removeEventListener("scroll", this.onDockScroll);
      window.removeEventListener("resize", this.onDockMeasure);
      window.removeEventListener("load", this.onDockMeasure);
      this.dockMq?.removeEventListener("change", this.onDockMqChange);
    }
  }
  customElements.define("bundle-bar", BundleBar);
}

// Track custom events for GA4
addEventListener("DOMContentLoaded", () => {
  const pdpReviewsContainer = document.querySelector(
    ".template-product #trustreviewsFrame",
  );
  if (!pdpReviewsContainer) return;

  pdpReviewsContainer.addEventListener("click", (e) => {
    if (e.target.closest("[data-tr-review-show-all]")) {
      const productTitle = document.querySelector(
        'input[name="product_title"]',
      )?.value;
      window.heistAnalytics?.pushExpandReviews({
        product_title: productTitle,
        product_id: window.ShopifyAnalytics?.meta?.product?.id?.toString(),
      });
    }
  });
});

// Menu items are set up in the admin either as a path or as a full url on the
// live domain, and both have to end up as the same value in a report.
function navUrl(link) {
  const url = new URL(link.href, window.location.href);
  return url.host === window.location.host
    ? url.pathname
    : url.host + url.pathname;
}

document.addEventListener("click", (e) => {
  // The mobile drawer re-dispatches a click on the expand arrow whenever a
  // parent item is tapped, which would report the same tap twice.
  if (!e.isTrusted) return;

  let link = e.target.closest("a[href]");
  // The arrow belongs to the item next to it, so tapping either is one tap
  // on that item.
  if (link?.matches(".navigation__children-toggle")) {
    link = link.parentElement?.querySelector("a.navigation__link");
  }

  // Three separate containers: #main-nav holds the mega menu panels, the
  // first level the visitor actually clicks lives in #proxy-nav, and the
  // mobile drawer is built from a template and inserted next to the header.
  const nav = link?.closest("#main-nav, #proxy-nav, .mobile-navigation-drawer");
  if (!nav) return;

  // Back, close and shade are anchors too, but they are not menu items.
  if (link.matches(".mobile-nav-back, .mobile-nav-toggle, .header-shade")) {
    return;
  }

  const promo = link.closest(
    ".menu-promotion, .mobile-menu-image, .navigation__column--promotion-left",
  );
  // A promotion built around an image carries no text inside the link itself,
  // so the label has to come from the block around it.
  const label =
    link.textContent.trim() ||
    promo?.querySelector(".menu-promotion__text")?.textContent ||
    promo?.querySelector(".mobile-featured-collection")?.textContent ||
    promo?.querySelector("p")?.textContent ||
    "";

  const image = link.querySelector("img");
  const imageUrl = (image?.currentSrc || image?.src)
    ?.split("?")[0]
    .replace(window.location.origin, "");
  const navPageType = window.ShopifyAnalytics?.meta?.page?.pageType;

  window.heistAnalytics?.pushClickNavigation({
    menu_type: link.closest(".mobile-navigation-drawer") ? "mobile" : "desktop",
    link_type: promo ? "promotion" : "link",
    ...(label ? { link_text: label.replace(/\s+/g, " ").trim() } : {}),
    // A parent item that only opens a submenu points at "#", which would
    // otherwise be reported as the current page.
    ...(link.getAttribute("href") === "#" ? {} : { link_url: navUrl(link) }),
    ...(imageUrl
      ? { image_url: imageUrl, image_name: imageUrl.split("/").pop() }
      : {}),
    page_path: window.location.pathname,
    ...(navPageType ? { page_type: navPageType } : {}),
  });
});

document.addEventListener("click", (e) => {
  const section = e.target.closest(".section-slideshow, .section-gallery");
  if (!section) return;

  // A banner leads somewhere either through its button or, when the whole
  // image is a link, through the wrapping anchor. The button sits inside that
  // anchor, so it has to be checked first.
  const button = e.target.closest(".text-overlay__button");
  const banner = e.target.closest("a.image-overlay__image-link");
  if (!button && !banner) return;

  const slide = e.target.closest(".slideshow__slide, .gallery__item");
  const image = slide?.querySelector("img");
  // Kept domain-less and without the screen-dependent size argument, so that
  // every value stays inside the 100 characters GA4 keeps for a parameter.
  const imageUrl = (image?.currentSrc || image?.src)
    ?.split("?")[0]
    .replace(window.location.origin, "");
  const pageType = window.ShopifyAnalytics?.meta?.page?.pageType;

  window.heistAnalytics?.pushClickPromoBanner({
    section_type: section.classList.contains("section-slideshow")
      ? "slideshow"
      : "gallery",
    click_area: button ? "button" : "image",
    ...(button ? { button_label: button.textContent.trim() } : {}),
    ...(imageUrl
      ? { image_url: imageUrl, image_name: imageUrl.split("/").pop() }
      : {}),
    page_path: window.location.pathname,
    ...(pageType ? { page_type: pageType } : {}),
  });
});
